import type { CityConfig } from "../city-template/types";

export const thousandOaksConfig: CityConfig = {
  city: "Thousand Oaks",
  state: "California",
  stateAbbr: "CA",
  slug: "thousand-oaks",
  route: "/cities/thousand-oaks",
  title: "Emergency Passport Thousand Oaks, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Thousand Oaks, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Ventura County and Conejo Valley travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Thousand Oaks, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Thousand Oaks and Conejo Valley travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Thousand%20Oaks%20California%20suburban%20cityscape%20at%20golden%20hour%20with%20rolling%20hills%2C%20oak%20trees%2C%20modern%20planned%20community%20buildings%2C%20The%20Oaks%20shopping%20mall%20visible%2C%20warm%20amber%20sunset%20light%20over%20the%20Conejo%20Valley%2C%20professional%20landscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20upscale%20suburban%20Southern%20California%20environment&width=1800&height=700&seq=thousand-oaks-hero-001&orientation=landscape",
  heroImageAlt: "Thousand Oaks California suburban landscape at golden hour with rolling hills and oak trees in the Conejo Valley",
  geoPosition: "34.1706;-118.8376",
  geoPlacename: "Thousand Oaks, California",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "300 N Los Angeles St, Los Angeles, CA 90012",
    distance: "Located in downtown LA — approximately 45–60 minutes from Thousand Oaks via US-101",
  },
  nearbyCitiesZips: [
    { name: "Thousand Oaks", zips: "91319, 91320, 91358, 91360, 91361, 91362" },
    { name: "Westlake Village", zips: "91359, 91361, 91362" },
    { name: "Agoura Hills", zips: "91301, 91376, 91377" },
    { name: "Oak Park", zips: "91377" },
    { name: "Simi Valley", zips: "93063, 93065" },
    { name: "Camarillo", zips: "93010, 93012" },
    { name: "Newbury Park", zips: "91320" },
    { name: "Moorpark", zips: "93021" },
    { name: "Calabasas", zips: "91301, 91302" },
    { name: "Ventura", zips: "93001–93007" },
    { name: "Oxnard", zips: "93030, 93033, 93035, 93036" },
    { name: "Malibu", zips: "90263, 90265" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Conejo Valley departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "Ventura", route: "/cities/ventura" },
    { name: "Santa Barbara", route: "/cities/santa-barbara" },
    { name: "Santa Monica", route: "/cities/santa-monica" },
    { name: "Irvine", route: "/cities/irvine" },
    { name: "Pasadena", route: "/cities/pasadena" },
    { name: "Anaheim", route: "/cities/anaheim" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Thousand Oaks remotely via secure courier. We do not have a walk-in office in Thousand Oaks — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Conejo Valley travelers.",
  fpcOfficeAddress: "225 S Lake Ave, Pasadena, CA 91101",
  fpcOfficeDistance: "Serving all of Ventura County and Conejo Valley",
  serviceAreaIntro: "From the Lynn Ranch area to Dos Vientos, from the Conejo Valley to the Santa Monica Mountains foothills — Fast Passport Center serves the entire Thousand Oaks community. We regularly help travelers from Westlake Village, Agoura Hills, Simi Valley, and Camarillo get their passports fast.",
  nearbyCitiesIntro: "We serve all Thousand Oaks neighborhoods and nearby Conejo Valley and Ventura County cities including Westlake Village, Agoura Hills, Oak Park, Simi Valley, Camarillo, and Moorpark. If you are in Ventura County, we have you covered.",
  howItWorksIntro: "Most Thousand Oaks travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With Malibu and Santa Barbara nearby, Thousand Oaks travelers are frequent international flyers. Here are the most common passport mistakes we see.",
  testimonialsIntro: "Thousand Oaks and Conejo Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Thousand Oaks? Here are the most common ones we hear from Conejo Valley travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Conejo Valley international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Jonathan M.",
      location: "Thousand Oaks, CA",
      rating: 5,
      text: "My flight to Barcelona was in 2 days and my passport was expired. Fast Passport Center got me a new one in 30 hours. Absolutely saved our family vacation.",
      service: "Emergency Passport",
    },
    {
      name: "Rebecca K.",
      location: "Westlake Village, CA",
      rating: 5,
      text: "Needed to renew for a business trip to Singapore. The process was incredibly smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Thomas B.",
      location: "Agoura Hills, CA",
      rating: 5,
      text: "Lost my passport the week before my honeymoon in Greece. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Catherine D.",
      location: "Simi Valley, CA",
      rating: 5,
      text: "As a frequent international traveler from Ventura County, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "to-1",
      name: "Thousand Oaks Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "325 E Hillcrest Dr",
      city: "Thousand Oaks",
      state: "CA",
      zip: "91360",
      phone: "(805) 496-6419",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from The Oaks Mall",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "to-2",
      name: "Simi Valley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "900 Cochran St",
      city: "Simi Valley",
      state: "CA",
      zip: "93065",
      phone: "(805) 522-4182",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "12 miles from Thousand Oaks",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "to-3",
      name: "Camarillo Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "300 E Pleasant Valley Rd",
      city: "Camarillo",
      state: "CA",
      zip: "93010",
      phone: "(805) 482-5441",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles from Thousand Oaks",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular location for Ventura County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "to-4",
      name: "Ventura Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "675 E Santa Clara St",
      city: "Ventura",
      state: "CA",
      zip: "93001",
      phone: "(805) 648-3341",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "20 miles from Thousand Oaks",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for coastal Ventura County residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "to-5",
      name: "Oxnard Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1901 N C St",
      city: "Oxnard",
      state: "CA",
      zip: "93030",
      phone: "(805) 486-4107",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "25 miles from Thousand Oaks",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Oxnard and Port Hueneme residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "to-6",
      name: "Ventura County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "800 S Victoria Ave",
      city: "Ventura",
      state: "CA",
      zip: "93009",
      phone: "(805) 654-2263",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "20 miles from Thousand Oaks",
      website: "https://recorder.countyofventura.org/",
      websiteLabel: "Ventura County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Ventura County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Ventura County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Ventura County applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Conejo Valley families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Conejo Valley travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Thousand Oaks." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Thousand Oaks address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Thousand Oaks before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Thousand Oaks for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Ventura County travelers." },
    { q: "How fast can I renew my passport in Thousand Oaks?", a: "Fast Passport Center offers fast passport renewal in Thousand Oaks in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Thousand Oaks in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Thousand Oaks?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Thousand Oaks residents." },
    { q: "Can I expedite a child passport in Thousand Oaks?", a: "Yes. Fast Passport Center expedites child passports in Thousand Oaks for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Thousand Oaks with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Thousand Oaks?",
      a: "With Fast Passport Center's emergency courier service, Thousand Oaks travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Los Angeles Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Thousand Oaks?",
      a: "Popular options include the Thousand Oaks Post Office (91360), Simi Valley Post Office (93065), Camarillo Post Office (93010), and Ventura County Clerk (93009). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Thousand Oaks?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Westlake Village or Agoura Hills?",
      a: "We serve all Conejo Valley and Ventura County communities including Westlake Village, Agoura Hills, Oak Park, Simi Valley, Camarillo, and Moorpark. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Thousand Oaks?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Thousand Oaks?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Thousand Oaks and Westlake Village offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Thousand Oaks?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Thousand Oaks?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Conejo Valley families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Thousand Oaks", address: "1600 E Thousand Oaks Blvd, Thousand Oaks, CA 91362", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Westlake Village", address: "30821 E Thousand Oaks Blvd, Westlake Village, CA 91362", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Thousand Oaks", address: "1000 E Thousand Oaks Blvd, Thousand Oaks, CA 91362", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Agoura Hills", address: "29360 Agoura Rd, Agoura Hills, CA 91301", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Simi Valley", address: "255 Cochran St, Simi Valley, CA 93065", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Camarillo", address: "580 E Ventura Blvd, Camarillo, CA 93010", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Thousand Oaks", address: "2410 E Thousand Oaks Blvd, Thousand Oaks, CA 91362", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Oxnard", address: "2001 E Ventura Blvd, Oxnard, CA 93036", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Thousand Oaks",
      address: "3070 E Thousand Oaks Blvd, Thousand Oaks, CA 91362",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Simi Valley",
      address: "1783 E Los Angeles Ave, Simi Valley, CA 93065",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Camarillo",
      address: "2420 E Gonzales Rd, Oxnard, CA 93036",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Oxnard",
      address: "3810 Saviers Rd, Oxnard, CA 93033",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Ventura County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "BUR",
      name: "Hollywood Burbank Airport",
      distance: "35 miles from Thousand Oaks",
      address: "2627 N Hollywood Way, Burbank, CA 91505",
      note: "The closest major airport for Thousand Oaks travelers. Domestic flights and some international connections. Accessible via US-101.",
      flightUrl: "https://www.hollywoodburbankairport.com/",
      flightLabel: "Burbank Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "45 miles from Thousand Oaks",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via US-101 and I-405.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "SBA",
      name: "Santa Barbara Airport",
      distance: "50 miles from Thousand Oaks",
      address: "500 James Fowler Rd, Santa Barbara, CA 93117",
      note: "Convenient for coastal destinations and domestic flights. Less crowded than LAX. Accessible via US-101 north.",
      flightUrl: "https://www.santabarbaraca.gov/",
      flightLabel: "Santa Barbara Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      label: "California Dept. of Public Health (Vital Records)",
      description: "State-issued birth and death certificates for all California counties",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      label: "Ventura County Clerk-Recorder (Birth, Death, Marriage Records)",
      description: "Birth certificates, death certificates, and marriage records for Ventura County",
      url: "https://www.ventura.org/recordings/",
    },
    {
      label: "Ventura County Superior Court (Divorce Records)",
      description: "Divorce decrees and court records for Ventura County",
      url: "https://www.ventura.courts.ca.gov/",
    },
  ],
};